import { RefObject, useCallback, useEffect, useRef, useState } from "react";
import { DayView, FULL_DAY_VIEW } from "./dayGridHelpers";

// Below this the gesture is a click (open the inspector), above it a drag.
const DRAG_THRESHOLD_PX = 4;

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

/** A cell of the grid, addressed by table and row. */
export interface GridPoint {
  table: number;
  row: number;
}

export interface DragState {
  bookingId: string;
  // Rows between the booking's start and the point that was grabbed, so a block
  // keeps its grip under the cursor instead of jumping its start to the pointer.
  grabOffsetRows: number;
  point: GridPoint | null;
  // Only true once the pointer has actually moved: lets a plain click through.
  active: boolean;
}

interface UseGridDragArgs {
  tables: number[];
  /** The columns area — its box must cover exactly the grid's cells. */
  gridRef: RefObject<HTMLElement>;
  /**
   * The rows the grid is currently showing. The pointer maths divides the
   * container by this many rows, so it must match what `DayGrid` renders.
   */
  view?: DayView;
  /** The unassigned tray; dropping here frees the booking's table. */
  trayRef?: RefObject<HTMLElement>;
  onDrop: (bookingId: string, point: GridPoint, grabOffsetRows: number) => void;
  onDropToTray?: (bookingId: string) => void;
}

const isInside = (
  element: HTMLElement | null,
  clientX: number,
  clientY: number,
): boolean => {
  if (!element) return false;
  const rect = element.getBoundingClientRect();
  return (
    clientX >= rect.left &&
    clientX <= rect.right &&
    clientY >= rect.top &&
    clientY <= rect.bottom
  );
};

/**
 * Pointer-driven dragging for the day grid. The grid is a uniform matrix, so the
 * cell under the pointer comes straight from the container's box — no per-cell
 * drop targets, which keeps 48 rows × N tables cheap and lets a booking be
 * dropped on any slot, open or not.
 */
export const useGridDrag = ({
  tables,
  gridRef,
  view = FULL_DAY_VIEW,
  trayRef,
  onDrop,
  onDropToTray,
}: UseGridDragArgs) => {
  const [drag, setDrag] = useState<DragState | null>(null);
  // Last pointer position, so the drop can tell the tray from the grid.
  const lastPointerRef = useRef<{ x: number; y: number } | null>(null);

  const dragRef = useRef<DragState | null>(null);
  dragRef.current = drag;
  const originRef = useRef<{ x: number; y: number } | null>(null);
  // Set when a gesture ended as a real drag, so the click it also fires can be
  // ignored and a moved booking doesn't pop its inspector open.
  const draggedRef = useRef(false);

  const pointAt = useCallback(
    (clientX: number, clientY: number): GridPoint | null => {
      const element = gridRef.current;
      if (!element || tables.length === 0) return null;
      const rect = element.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return null;

      const rowCount = view.endRow - view.startRow;
      const column = clamp(
        Math.floor((clientX - rect.left) / (rect.width / tables.length)),
        0,
        tables.length - 1,
      );
      const row = clamp(
        view.startRow +
          Math.floor((clientY - rect.top) / (rect.height / rowCount)),
        view.startRow,
        view.endRow - 1,
      );
      return { table: tables[column], row };
    },
    [gridRef, tables, view],
  );

  /**
   * Start dragging a booking. `absStartRow` is its start relative to the visible
   * day — negative for a booking that began the day before, which is what keeps
   * the grab offset right when the tail of a spill-over is the part grabbed.
   * Pass `null` for a booking with no position yet (dragged out of the tray): it
   * then lands exactly on the row it is dropped on, whatever rows the grid shows.
   */
  const beginDrag = useCallback(
    (
      event: React.PointerEvent,
      bookingId: string,
      absStartRow: number | null,
    ) => {
      const point = pointAt(event.clientX, event.clientY);
      if (!point) return;
      event.preventDefault();
      draggedRef.current = false;
      originRef.current = { x: event.clientX, y: event.clientY };
      setDrag({
        bookingId,
        grabOffsetRows: absStartRow === null ? 0 : point.row - absStartRow,
        point,
        active: false,
      });
    },
    [pointAt],
  );

  const isDragging = drag !== null;

  useEffect(() => {
    if (!isDragging) return undefined;

    const handleMove = (event: PointerEvent) => {
      const origin = originRef.current;
      const travelled = origin
        ? Math.hypot(event.clientX - origin.x, event.clientY - origin.y)
        : 0;
      lastPointerRef.current = { x: event.clientX, y: event.clientY };
      const point = pointAt(event.clientX, event.clientY);
      setDrag((previous) =>
        previous
          ? {
              ...previous,
              point: point ?? previous.point,
              active: previous.active || travelled > DRAG_THRESHOLD_PX,
            }
          : previous,
      );
    };

    const handleUp = () => {
      const current = dragRef.current;
      const pointer = lastPointerRef.current;
      if (current?.active) {
        draggedRef.current = true;
        if (
          onDropToTray &&
          pointer &&
          isInside(trayRef?.current ?? null, pointer.x, pointer.y)
        ) {
          onDropToTray(current.bookingId);
        } else if (current.point) {
          onDrop(current.bookingId, current.point, current.grabOffsetRows);
        }
      }
      originRef.current = null;
      lastPointerRef.current = null;
      setDrag(null);
    };

    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);
    window.addEventListener("pointercancel", handleUp);
    return () => {
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
      window.removeEventListener("pointercancel", handleUp);
    };
  }, [isDragging, onDrop, onDropToTray, pointAt, trayRef]);

  // Keep the page from selecting text under the cursor mid-drag.
  useEffect(() => {
    if (!drag?.active) return undefined;
    const previous = document.body.style.userSelect;
    document.body.style.userSelect = "none";
    return () => {
      document.body.style.userSelect = previous;
    };
  }, [drag?.active]);

  const consumeClickAfterDrag = useCallback((): boolean => {
    if (!draggedRef.current) return false;
    draggedRef.current = false;
    return true;
  }, []);

  return { drag, beginDrag, consumeClickAfterDrag };
};
